import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { id: "in_attesa", label: "In programma" },
  { id: "inviato", label: "Inviati" },
  { id: "problemi", label: "Da completare" },
  { id: "annullato", label: "Annullati" },
];

const FASE_LABELS = {
  programmato: "In programma",
  pronto: "Pronto all'invio",
  attesa_configurazione: "In attesa di configurazione",
  inviato: "Inviato",
  errore: "Errore di invio",
  dati_mancanti: "Email mancante",
  annullato: "Annullato",
};

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
}

function faseClass(fase) {
  if (fase === "pronto" || fase === "inviato") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (fase === "errore" || fase === "dati_mancanti") return "bg-rose-100 text-rose-800 border-rose-200";
  if (fase === "annullato") return "bg-slate-100 text-slate-600 border-slate-200";
  if (fase === "attesa_configurazione") return "bg-amber-100 text-amber-900 border-amber-200";
  return "bg-sky-100 text-sky-800 border-sky-200";
}

function countFor(counts, filterId) {
  if (!counts) return 0;
  if (filterId === "problemi") return (counts.errore || 0) + (counts.dati_mancanti || 0);
  return counts[filterId] || 0;
}

export default function TrustpilotAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingBrand, setSavingBrand] = useState("");
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("in_attesa");
  const [inviti, setInviti] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState("");

  const notifyMenu = () => {
    window.dispatchEvent(new Event("affidya-commerciale-menu-counts"));
  };

  const loadInviti = useCallback(async (stato) => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (stato) params.set("stato", stato);
      const res = await fetch(`${API}/api/admin/commerciale/trustpilot/inviti?${params}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento inviti");
      setInviti(Array.isArray(json.data) ? json.data : []);
      if (json.counts) setCounts(json.counts);
    } catch (error) {
      toast.error(error.message || "Errore caricamento inviti");
    } finally {
      setListLoading(false);
    }
  }, [token]);

  const load = useCallback(async (stato, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/commerciale/trustpilot`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento Trustpilot");
      const nextBrands = Array.isArray(json.brands) ? json.brands : [];
      setBrands(nextBrands);
      setDrafts(Object.fromEntries(nextBrands.map((brand) => [brand.brand, {
        enabled: Boolean(brand.enabled),
        afs_email: brand.afs_email || "",
        delay_days: brand.delay_days || 21,
      }])));
      setCounts(json.counts || {});
      notifyMenu();
      await loadInviti(stato);
    } catch (error) {
      toast.error(error.message || "Errore caricamento Trustpilot");
    } finally {
      setLoading(false);
    }
  }, [token, loadInviti]);

  const filterRef = useRef(filter);
  filterRef.current = filter;
  const skipFilterReload = useRef(true);

  useEffect(() => {
    load(filterRef.current);
  }, [load]);

  useEffect(() => {
    if (skipFilterReload.current) {
      skipFilterReload.current = false;
      return;
    }
    loadInviti(filter);
  }, [filter, loadInviti]);

  const setDraft = (brand, patch) => {
    setDrafts((current) => ({
      ...current,
      [brand]: { ...current[brand], ...patch },
    }));
  };

  const save = async (brand) => {
    const draft = drafts[brand];
    if (!draft) return;
    const delay = Number(draft.delay_days);
    if (!Number.isInteger(delay) || delay < 7 || delay > 90) {
      toast.error("I giorni di attesa devono essere tra 7 e 90");
      return;
    }
    setSavingBrand(brand);
    try {
      const res = await fetch(`${API}/api/admin/commerciale/trustpilot/${brand}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          enabled: Boolean(draft.enabled),
          afs_email: (draft.afs_email || "").trim(),
          delay_days: delay,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio");
      if (json.brand) {
        setBrands((current) => current.map((item) => (
          item.brand === json.brand.brand ? { ...item, ...json.brand } : item
        )));
        setDrafts((current) => ({
          ...current,
          [json.brand.brand]: {
            enabled: Boolean(json.brand.enabled),
            afs_email: json.brand.afs_email || "",
            delay_days: json.brand.delay_days || 21,
          },
        }));
      }
      if (json.counts) setCounts(json.counts);
      toast.success("Configurazione Trustpilot salvata");
      notifyMenu();
      await loadInviti(filter);
    } catch (error) {
      toast.error(error.message || "Errore salvataggio");
    } finally {
      setSavingBrand("");
    }
  };

  const cancelInvite = async (invitation) => {
    if (!window.confirm(`Annullare l'invito per ${invitation.recipient_name || invitation.recipient_email}?`)) return;
    setCancellingId(invitation.id);
    try {
      const res = await fetch(`${API}/api/admin/commerciale/trustpilot/inviti/${invitation.id}/annulla`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore annullamento");
      if (json.counts) setCounts(json.counts);
      toast.success("Invito annullato");
      notifyMenu();
      await loadInviti(filter);
    } catch (error) {
      toast.error(error.message || "Errore annullamento");
    } finally {
      setCancellingId("");
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Star className="h-5 w-5 text-emerald-600" />
          Trustpilot
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Automatic Feedback Service, senza API. Affidya prepara gli inviti e, a scadenza, spedisce
          l&apos;email tecnica all&apos;indirizzo AFS del profilo. Il cliente riceve il modello configurato su Trustpilot.
          Entra in coda ogni struttura con abbonamento attivo e ogni famiglia con pratica in stato Inserito, con lo stesso ritardo.
          Gli inviti già in scadenza partono al giro automatico successivo, circa ogni 15 minuti: controlla l&apos;elenco prima di attivare il profilo.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {brands.map((brand) => {
          const draft = drafts[brand.brand] || { enabled: false, afs_email: "", delay_days: 21 };
          return (
            <Card key={brand.brand}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{brand.label}</CardTitle>
                <p className="text-sm text-slate-500">{brand.domain}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">{brand.audience}</p>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor={`tp-enabled-${brand.brand}`}>Invio attivo</Label>
                  <Switch
                    id={`tp-enabled-${brand.brand}`}
                    checked={Boolean(draft.enabled)}
                    onCheckedChange={(value) => setDraft(brand.brand, { enabled: value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`tp-email-${brand.brand}`}>Indirizzo email AFS</Label>
                  <Input
                    id={`tp-email-${brand.brand}`}
                    type="email"
                    autoComplete="off"
                    placeholder="l'indirizzo unico indicato da Trustpilot"
                    value={draft.afs_email}
                    onChange={(event) => setDraft(brand.brand, { afs_email: event.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`tp-delay-${brand.brand}`}>Giorni di attesa prima dell&apos;invio</Label>
                  <Input
                    id={`tp-delay-${brand.brand}`}
                    type="number"
                    min={7}
                    max={90}
                    value={draft.delay_days}
                    onChange={(event) => setDraft(brand.brand, { delay_days: event.target.value })}
                  />
                  <p className="text-xs text-slate-500">Tra 7 e 90. Vale per gli inviti creati da questo momento.</p>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    {brand.updated_at ? `Aggiornato il ${formatWhen(brand.updated_at)}` : "Non ancora salvato"}
                  </p>
                  <Button size="sm" onClick={() => save(brand.brand)} disabled={savingBrand === brand.brand}>
                    {savingBrand === brand.brand ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                    Salva
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Inviti</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((item) => (
              <Button
                key={item.id}
                type="button"
                size="sm"
                variant={filter === item.id ? "default" : "outline"}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
                <span className="ml-2 tabular-nums">{countFor(counts, item.id)}</span>
              </Button>
            ))}
          </div>

          {listLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : inviti.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun invito in questo elenco.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-2 font-medium">Profilo</th>
                    <th className="px-2 py-2 font-medium">Destinatario</th>
                    <th className="px-2 py-2 font-medium">Stato</th>
                    <th className="px-2 py-2 font-medium">Invio previsto</th>
                    <th className="px-2 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {inviti.map((invitation) => (
                    <tr key={invitation.id} className="border-b border-slate-100">
                      <td className="px-2 py-3">
                        <div className="font-medium text-slate-800">{invitation.brand_label}</div>
                        <div className="text-xs text-slate-500">{invitation.domain}</div>
                      </td>
                      <td className="px-2 py-3">
                        <div>{invitation.recipient_name || "—"}</div>
                        <div className="text-xs text-slate-500">{invitation.recipient_email || "Email mancante"}</div>
                        {invitation.source_label && invitation.source_label !== invitation.recipient_name ? (
                          <div className="text-xs text-slate-500">{invitation.source_label}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-3">
                        <Badge variant="outline" className={faseClass(invitation.fase)}>
                          {FASE_LABELS[invitation.fase] || invitation.stato}
                        </Badge>
                        {invitation.errore ? (
                          <p className="mt-1 max-w-xs text-xs text-rose-700">{invitation.errore}</p>
                        ) : null}
                      </td>
                      <td className="px-2 py-3 text-slate-600">
                        {invitation.stato === "inviato" ? formatWhen(invitation.sent_at) : formatWhen(invitation.scheduled_for)}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {invitation.stato !== "inviato" && invitation.stato !== "annullato" ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={cancellingId === invitation.id}
                            onClick={() => cancelInvite(invitation)}
                          >
                            {cancellingId === invitation.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Annulla"}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
